import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useCapabilities } from '../hooks/useCapabilities';
import { describeError } from '../describeError';
import { HISTORY_PATH } from '../routes';
import type { Capabilities } from '../types';
import { ErrorSummary, StepHeading } from './basics';

interface Props {
  /** `create` additionally requires canCreate (wizard screens); `read` only needs canRead. */
  require: 'read' | 'create';
  children: (capabilities: Capabilities) => ReactNode;
}

/**
 * Display-only gate over GET /capabilities. It decides which states to SHOW; the server still enforces every
 * permission on every call. A caller without read arrives here as all-false capabilities.
 */
export function AccessGate({ require, children }: Props) {
  const { loading, data, error, reload } = useCapabilities();

  if (loading) {
    return (
      <p className="muted" role="status">
        Loading Payroll History Import…
      </p>
    );
  }
  if (error !== undefined || !data) {
    return (
      <div className="phi-state">
        <StepHeading>Import Payroll Data</StepHeading>
        <ErrorSummary message={describeError(error, 'Could not load Payroll History Import.')} />
        <button type="button" className="phi-btn-secondary" onClick={reload}>
          Try again
        </button>
      </div>
    );
  }
  if (!data.canRead) {
    return (
      <div className="phi-state">
        <StepHeading>Import Payroll Data</StepHeading>
        <p>You don&apos;t have access to Payroll History Import.</p>
        <p className="muted">Ask a company administrator to grant you access if you need to view or import payroll history.</p>
      </div>
    );
  }
  if (!data.enabled) {
    return (
      <div className="phi-state">
        <StepHeading>Import Payroll Data</StepHeading>
        <p>Payroll history import isn&apos;t enabled in this environment.</p>
      </div>
    );
  }
  if (require === 'create' && !data.canCreate) {
    return (
      <div className="phi-state">
        <StepHeading>Import Payroll Data</StepHeading>
        <p>You don&apos;t have permission to start or change a payroll history import.</p>
        <p>
          <Link to={HISTORY_PATH}>View imported payroll history</Link>
        </p>
      </div>
    );
  }
  return <>{children(data)}</>;
}
